import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import realYieldCard from '../../assets/real-yield-card-mockup.svg';
import '../../styles/features/homepage.css';

const Homepage: React.FC = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "Who is Babylon for?",
      answer: "Babylon is built for investors who already understand covered calls and want better tools to find opportunities, analyze contracts, and manage positions efficiently."
    },
    {
      question: "How much capital do I need to start?",
      answer: "Most covered call strategies require owning 100 shares of the underlying stock. With stocks typically ranging from $50-500 per share, you'll need $5,000-50,000 per position."
    },
    {
      question: "Is Babylon financial advice?",
      answer: "No. Babylon is a data and analytics platform — we surface signals, you make every trading decision. Past performance doesn't guarantee future results. Consult a financial advisor before investing."
    },
    {
      question: "How does this compare to buying JEPI/QYLD?",
      answer: "Unlike ETFs where a fund manager makes all decisions, Babylon gives you the data and tools to make your own informed choices—with full control over stock selection, strike prices, and timing."
    }
  ];

  const features = [
    {
      title: 'CC Score v7.2',
      text: '100-point algorithm validated against a 489-stock historical backtest. Two dimensions — Quality and Timing — with full transparency on every sub-score.'
    },
    {
      title: 'Cluster-Setup Engine',
      text: '12 behavioral clusters × 27 setup cells. See the historical good / neutral / risk label for the exact regime your stock is in right now.'
    },
    {
      title: 'Stock Screener',
      text: 'Filter 1,000+ stocks by IV rank, yield, sector, liquidity, and CC suitability. Apply your framework, not ours.'
    },
    {
      title: 'Contract Analysis',
      text: 'Compare strikes and expirations with real-time Greeks, premium yields, and annualized returns. Pick the contract that fits your thesis.'
    },
    {
      title: 'Position Management Engine',
      text: 'A 6-rule playbook surfaces clear signals — Hull Breach, Theta Win, Upside Watch — for every open position. You decide the action.'
    },
    {
      title: 'AI Brief + Performance',
      text: 'Morning Brief and Daily Digest summarize your portfolio. Track realized income, win rate, and yield over time.'
    }
  ];

  return (
    <div className="homepage">
      {/* Navigation */}
      <nav className="homepage-nav">
        <div className="homepage-nav-container">
          <div className="homepage-nav-brand">
            <img src="/images/branding/babylon-logo.svg" alt="Babylon" className="homepage-nav-logo" />
            <span className="homepage-nav-name">Babylon</span>
          </div>
          <div className="homepage-nav-actions">
            <Link to="/login" className="btn-ghost">Log In</Link>
            <Link to="/register?plan=pro" className="btn-primary">Try Pro free</Link>
          </div>
        </div>
      </nav>

      {/* Hero Section 1 - Video Background */}
      <section className="homepage-hero-video">
        {/* Video Background */}
        <div className="homepage-hero-video-bg">
          <video
            autoPlay
            muted
            loop
            playsInline
          >
            <source src="/videos/hero-background.mp4" type="video/mp4" />
          </video>
        </div>

        {/* Content */}
        <div className="homepage-hero-content">
          <div className="homepage-hero-inner">
            <div className="homepage-hero-text">
              <h1 className="homepage-hero-title hp-reveal" style={{ '--d': '60ms' } as React.CSSProperties}>
                Covered Calls, on Your Terms.
              </h1>
              <p className="homepage-hero-subtitle hp-reveal" style={{ '--d': '150ms' } as React.CSSProperties}>
                A data-driven platform for self-directed covered call investors. We surface the data; you make the call.
              </p>
              <div className="homepage-hero-actions hp-reveal" style={{ '--d': '240ms' } as React.CSSProperties}>
                <Link to="/register?plan=pro" className="btn-primary btn-large">Try Pro free for 14 days</Link>
              </div>
            </div>
            <div className="homepage-hero-spacer">
              {/* Empty space for video background to show through */}
            </div>
          </div>
        </div>
      </section>

      {/* Real Yield wedge — mirrors the /real-yield landing */}
      <section className="homepage-section homepage-realyield">
        <div className="homepage-realyield-inner">
          <div className="homepage-realyield-text">
            <div className="homepage-split-rule" aria-hidden="true">
              <span className="homepage-split-phantom" />
              <span className="homepage-split-real" />
            </div>
            <h2 className="homepage-realyield-title">
              Is your income ETF <span className="homepage-realyield-mark">quietly eating</span> your principal?
            </h2>
            <p className="homepage-realyield-sub">
              You can't answer that until your "income" number is honest. Most aren't.
            </p>
            <Link to="/real-yield" className="homepage-realyield-link">
              See the honest number →
            </Link>
          </div>
          <div className="homepage-realyield-card">
            <img
              src={realYieldCard}
              alt="Real Yield — about 40% of my recorded covered-call income was phantom; $18,069 of $40,498. My own account, as of 9 Jun 2026, not a projection."
            />
          </div>
        </div>
      </section>

      {/* Hero Section 2 - Full-width Dashboard */}
      <section className="homepage-hero-product">
        {/* Content overlay */}
        <div className="homepage-hero-product-content">
          <div className="homepage-hero-product-text">
            <h2 className="homepage-hero-product-title">
              All your data. One place.
            </h2>
            <p className="homepage-hero-product-subtitle">
              Stop juggling spreadsheets, scanners, and broker screens. Babylon consolidates options data, Greeks, and analytics so you can apply your framework with better data.
            </p>
            <div className="homepage-hero-product-actions">
              <Link to="/register?plan=pro" className="btn-primary btn-large">Try Pro free for 14 days</Link>
              <Link to="/login" className="btn-outline btn-large homepage-btn-outline-light">Log In</Link>
            </div>
          </div>
        </div>

        {/* Full-width dashboard image */}
        <div className="homepage-hero-product-image">
          <img
            src="/images/hero/portfolio-dashboard.png"
            alt="Babylon portfolio tracking — live position cards with discipline rules, P&L breakdown, decision maps, and Greeks"
          />
        </div>
      </section>

      {/* Problem/Solution Section */}
      <section className="homepage-section">
        <h2 className="homepage-section-title">
          Built for Investors Who Make Their Own Calls.
        </h2>
        <div className="homepage-grid-3">
          <div className="homepage-card">
            <h3 className="homepage-card-title">
              Data, Not Opinions.
            </h3>
            <p className="homepage-card-text">
              Options data is scattered across brokers, scanners, and feeds. Babylon pulls it together so you can evaluate opportunities on your own terms — not a fund manager's.
            </p>
          </div>

          <div className="homepage-card">
            <h3 className="homepage-card-title">
              One Unified View.
            </h3>
            <p className="homepage-card-text">
              Price action, Greeks, IV metrics, fundamentals, and behavioral context — in a single dashboard. Filter by the criteria that matter to your framework.
            </p>
          </div>

          <div className="homepage-card">
            <h3 className="homepage-card-title">
              Your Framework, Faster.
            </h3>
            <p className="homepage-card-text">
              Compare contracts side-by-side, assess premium quality, and track every position. The signals are surfaced; the decision stays yours.
            </p>
          </div>
        </div>
      </section>

      {/* Core Features Section */}
      <section className="homepage-section">
        <h2 className="homepage-section-title">
          Everything You Need to Execute Covered Calls
        </h2>
        <div className="homepage-grid-3">
          {features.map((f, i) => (
            <div key={f.title} className="homepage-card homepage-card--feature">
              <span className="homepage-card-index">{String(i + 1).padStart(2, '0')}</span>
              <h3 className="homepage-card-title">{f.title}</h3>
              <p className="homepage-card-text">{f.text}</p>
            </div>
          ))}
        </div>
      </section>


      {/* Comparison Section */}
      <section className="homepage-section">
        <h2 className="homepage-section-title">
          Babylon vs. Covered Call ETFs
        </h2>
        <div className="homepage-table-container">
          <div className="homepage-table-scroll">
            <table className="homepage-table">
              <thead>
                <tr>
                  <th>Feature</th>
                  <th className="is-center">Covered Call ETFs</th>
                  <th className="is-center is-brand">Babylon</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Pricing</td>
                  <td className="is-center is-bad">0.35–0.68% a year<br /><small>JEPQ 0.35% · QQQI 0.68%</small></td>
                  <td className="is-center is-good">$249/year</td>
                </tr>
                <tr>
                  <td>Data & Analytics</td>
                  <td className="is-center is-bad">No</td>
                  <td className="is-center is-good">Yes</td>
                </tr>
                <tr>
                  <td>Strategy Control</td>
                  <td className="is-center is-bad">None</td>
                  <td className="is-center is-good">Full</td>
                </tr>
                <tr>
                  <td>Stock Selection</td>
                  <td className="is-center is-bad">Fund chooses</td>
                  <td className="is-center is-good">You choose</td>
                </tr>
                <tr>
                  <td>Strike/Expiration Control</td>
                  <td className="is-center is-bad">None</td>
                  <td className="is-center is-good">Complete</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="homepage-section">
        <h2 className="homepage-section-title">
          Simple Pricing
        </h2>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', justifyContent: 'center', alignItems: 'stretch', maxWidth: 880, margin: '0 auto' }}>
          {[
            { name: 'Free', price: '$0', sub: 'forever', blurb: 'Screener + scores, a taste of AI, and a read-only portfolio. See the gold.', cta: 'Start free', primary: false, disabled: false },
            { name: 'Pro', price: '$249', sub: 'per year', blurb: 'Real-Yield, roll-vs-assign, benchmark lens, Discipline & unlimited AI. Act on your money.', cta: 'Try Pro free for 14 days', primary: true, disabled: false },
            { name: 'Premium', price: '$420', sub: 'per year', blurb: 'Tax-aware decisions + the deepest position-aware AI. Coming soon.', cta: 'Coming soon', primary: false, disabled: true },
          ].map((t) => (
            <div key={t.name} style={{ flex: '1 1 240px', maxWidth: 280, border: t.primary ? '2px solid #2962ff' : '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: '22px 20px', background: t.primary ? 'linear-gradient(180deg, rgba(41,98,255,0.10), transparent 60%), #181818' : '#181818', boxShadow: t.primary ? '0 16px 44px rgba(41,98,255,0.24)' : 'none', display: 'flex', flexDirection: 'column', textAlign: 'center' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#2962ff', textTransform: 'uppercase', letterSpacing: 0.5 }}>{t.name}</div>
              <div style={{ margin: '8px 0 2px' }}><span style={{ fontSize: 30, fontWeight: 800, color: '#f2f2f0' }}>{t.price}</span> <span style={{ fontSize: 13, color: '#8a8a86' }}>{t.sub}</span></div>
              <p style={{ fontSize: 13.5, color: '#b8b8b4', lineHeight: 1.5, margin: '10px 0 18px', flex: 1 }}>{t.blurb}</p>
              {t.disabled ? (
                <span style={{ color: '#8a8a86', fontSize: 14, fontWeight: 600 }}>{t.cta}</span>
              ) : (
                <Link to={t.primary ? '/register?plan=pro' : '/register'} className={t.primary ? 'btn-primary' : ''} style={!t.primary ? { color: '#2962ff', fontWeight: 600, textDecoration: 'none', border: '1.5px solid #2962ff', borderRadius: 8, padding: '10px' } : undefined}>{t.cta}</Link>
              )}
            </div>
          ))}
        </div>
        <p className="homepage-pricing-disclaimer" style={{ textAlign: 'center', marginTop: 18 }}>
          Anchored to ~0.1% of a $250k portfolio — less than an income ETF's expense ratio. <Link to="/pricing">See full plans →</Link>
        </p>
      </section>

      {/* FAQ Section */}
      <section className="homepage-section">
        <h2 className="homepage-section-title">
          Common Questions
        </h2>
        <div className="homepage-faq-container">
          {faqs.map((faq, index) => (
            <div key={index} className="homepage-faq-item">
              <button
                onClick={() => toggleFaq(index)}
                className="homepage-faq-button"
                aria-expanded={openFaq === index}
              >
                {faq.question}
                <span className={`homepage-faq-icon${openFaq === index ? ' is-open' : ''}`}>
                  +
                </span>
              </button>
              {openFaq === index && (
                <div className="homepage-faq-answer">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="homepage-final-cta">
        <div className="homepage-final-cta-inner">
          <h2 className="homepage-final-cta-title">
            Ready to Make Better Covered Call Decisions?
          </h2>
          <p className="homepage-final-cta-sub">
            Join self-directed investors who use Babylon to apply their own framework with the data and signals they need.
          </p>
          <Link to="/register?plan=pro" className="btn-primary btn-large">
            Try Pro free for 14 days · no card
          </Link>
          <p className="homepage-final-cta-note">
            Risk disclosure and terms apply
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="homepage-footer">
        <div className="homepage-footer-content">
          <div className="homepage-footer-brand">
            <img src="/images/branding/babylon-logo.svg" alt="Babylon" className="homepage-footer-logo" />
            <span className="homepage-footer-name">Babylon</span>
          </div>
          <p className="homepage-footer-disclaimer">
            Babylon is a data aggregation and analytics platform. Content does not constitute investment advice.
            Past performance does not guarantee future results. Consult a financial advisor before making investment decisions.
          </p>
          <div className="homepage-footer-copyright">
            © 2026 Babylon.markets. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Homepage;
